import {
  useCreateDashboardFolderMutation,
  useListDashboardFoldersQuery,
  useListDashboardsQuery,
  type ChatArtifact,
  type Dashboard
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../components/ui/button'
import { AGENT_INSIGHTS_FOLDER_PATH } from '../DashboardsPage/explorer-paths'
import SaveArtifactDialog from './SaveArtifactDialog'

const DISABLED_DASHBOARD_PATHS = new Set([AGENT_INSIGHTS_FOLDER_PATH])

interface SaveToDashboardsDialogProps {
  open: boolean
  artifact: ChatArtifact
  canMakeLive: boolean
  onClose: () => void
  onSaved: (dashboard: Dashboard) => void
  onMakeLive: () => void
}

export default function SaveToDashboardsDialog({
  open,
  artifact,
  canMakeLive,
  onClose,
  onSaved,
  onMakeLive
}: SaveToDashboardsDialogProps) {
  const foldersQuery = useListDashboardFoldersQuery()
  const dashboardsQuery = useListDashboardsQuery()
  const [createDashboardFolder] = useCreateDashboardFolderMutation()

  return (
    <SaveArtifactDialog
      open={open}
      artifact={artifact}
      library="Dashboards"
      itemNoun="dashboard"
      description="Choose where this artifact should appear in Dashboards."
      idPrefix="artifact-dashboard"
      folders={foldersQuery.data ?? []}
      items={dashboardsQuery.data ?? []}
      disabledPaths={DISABLED_DASHBOARD_PATHS}
      createFolder={(input) => createDashboardFolder(input).unwrap()}
      toItemName={(name) => name.trim()}
      banner={(submitting) => (
        <div className="rounded-md bg-bg-sunk px-3 py-2 text-xs text-ink-2">
          {artifact.fetch_mode === 'live' ? (
            'Live — the saved dashboard keeps refreshing.'
          ) : (
            <div className="flex items-center justify-between gap-3">
              <span>
                Snapshot — the saved dashboard stays on this artifact’s fixed time window.
              </span>
              <Button
                variant="link"
                size="sm"
                className="h-auto shrink-0 p-0 text-xs"
                disabled={submitting || !canMakeLive}
                title={
                  canMakeLive ? undefined : 'You can view this conversation but not post in it.'
                }
                onClick={onMakeLive}
              >
                Make it live first
              </Button>
            </div>
          )}
        </div>
      )}
      onClose={onClose}
      onSaved={(saved) => onSaved(saved as Dashboard)}
    />
  )
}
